import { createClient } from '@/lib/supabase/client';

export interface AboutContent {
  id: string;
  sectionKey: string;
  sectionTitle: string;
  content: any;
  createdAt: string;
  updatedAt: string;
}

export interface AboutContentInput {
  sectionKey: string;
  sectionTitle: string;
  content: any;
}

class AboutService {
  async getAll(): Promise<AboutContent[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('about_us_content')
      .select('*')
      .order('section_key', { ascending: true });

    if (error) throw new Error(error.message);
    
    return (data || []).map(this.mapFromDb);
  }

  async getByKey(sectionKey: string): Promise<AboutContent | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('about_us_content')
      .select('*')
      .eq('section_key', sectionKey)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(error.message);
    }
    
    return data ? this.mapFromDb(data) : null;
  }

  async upsert(input: AboutContentInput): Promise<AboutContent | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('about_us_content')
      .upsert([{
        section_key: input.sectionKey,
        section_title: input.sectionTitle,
        content: input.content,
        updated_by: user.id,
      }], {
        onConflict: 'section_key',
      })
      .select()
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async update(sectionKey: string, content: any): Promise<AboutContent | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('about_us_content')
      .update({
        content,
        updated_by: user.id,
      })
      .eq('section_key', sectionKey)
      .select()
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async delete(sectionKey: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('about_us_content')
      .delete()
      .eq('section_key', sectionKey);

    if (error) throw new Error(error.message);
  }

  private mapFromDb(data: any): AboutContent {
    return {
      id: data.id,
      sectionKey: data.section_key,
      sectionTitle: data.section_title,
      content: data.content,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
}

export const aboutService = new AboutService();